/**
 * =============================================================================
 * theme.js
 * =============================================================================
 * Entry point for all theme Javascript.
 * 
 * Before launching a site, consider what imports you actually need - i.e. which
 * parts of Bootstrap, which vendor JS imports, which Bootstrap plugins, etc.
 * =============================================================================
 */

// Imports from node_modules, installed via 'yarn add ...'
import $ from 'jquery'; // (~89Kb)
import * as bootstrap from 'bootstrap'; // (~97Kb)
import Swiper, { Navigation, Pagination, Autoplay, Thumbs } from 'swiper'; // Swiper (~78Kb)
import 'waypoints/lib/noframework.waypoints.min.js'; // (~10Kb)
import Cookies from 'js-cookie'; // (~2Kb)
import AOS from 'aos'; // Animate on Scroll (~14Kb)

import Gauge from 'svg-gauge'; // Animated Gauges (~3Kb)
import hcSticky from 'hc-sticky'; // Sticky product gallery on desktop (~11Kb)
// import DepartureBoard from './departure-board'; // Departure board effect (~4Kb)

Swiper.use([Navigation, Pagination, Autoplay, Thumbs]);

// Export for others scripts to use by attaching to the windowd
window.$ = $;
window.jQuery = $;
window.bootstrap = bootstrap;
window.Swiper = Swiper;
window.SwiperModules = { Navigation, Pagination, Autoplay, Thumbs };
window.Cookies = Cookies;

/**
 * Minimal Stencil shim.
 * Satisfies `<swiper-slider>` (build/scripts/elements/swiper-slider.js), which
 * comes from a newer sibling project that uses a Stencil runtime. The full
 * runtime lives in build/scripts/bits-wed-need/stencil.js; here we expose only
 * what that element touches so we can keep the element byte-identical.
 */
window.Stencil = {
  ensureVendor(name) {
    if (name === "swiper") return Promise.resolve(window.Swiper);
    return Promise.reject(new Error(`[Stencil shim] No vendor loader for '${name}'`));
  },

  getStencilVariable(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(`--st-${name}`);
  },

  remToPx(rem) {
    rem = parseFloat(rem);
    return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
  },

  getBSBreakpoints() {
    const rootStyles = getComputedStyle(document.documentElement);
    return ["sm", "md", "lg", "xl", "xxl", "xxxl"].reduce(
      (breakpoints, bp) => {
        breakpoints[bp] = parseInt(
          rootStyles.getPropertyValue(`--bs-breakpoint-${bp}`).trim(),
          10
        );
        return breakpoints;
      },
      { xs: 0 }
    );
  },

  colClassToItemsPerRow(classString = "") {
    const breakpoints = ["xs", "sm", "md", "lg", "xl", "xxl", "xxxl"];
    const defaultColumns = 12;
    const customClasses = { "⅒": 10, "⅛": 8, "⅕": 5, "3_10": 3.33 };

    const itemsPerRow = Object.fromEntries(breakpoints.map((bp) => [bp, 1]));
    const classes = classString.trim().split(/\s+/).filter(Boolean);

    classes.forEach((cls) => {
      const mCol = cls.match(/^col(?:-([a-z]{2,3}))?-(\d+|auto)$/);
      if (mCol) {
        const bp = mCol[1] || "xs";
        const spec = mCol[2];
        if (spec === "auto") {
          itemsPerRow[bp] = "auto";
        } else {
          const size = parseInt(spec, 10);
          if (size > 0) itemsPerRow[bp] = defaultColumns / size;
        }
        return;
      }

      if (cls === "col") {
        itemsPerRow["xs"] = "auto";
        return;
      }

      const mColx = cls.match(/^colx(?:-([a-z]{2,3}))?-(.+)$/);
      if (mColx) {
        const bp = mColx[1] || "xs";
        const key = mColx[2];
        if (customClasses[key] != null) {
          itemsPerRow[bp] = customClasses[key];
        }
      }
    });

    for (let i = 1; i < breakpoints.length; i++) {
      const prevBp = breakpoints[i - 1];
      const currentBp = breakpoints[i];
      const hasExplicit = classes.some((c) =>
        new RegExp(`-(?:${currentBp})(?:-|$)`).test(c)
      );
      if (!hasExplicit) {
        itemsPerRow[currentBp] = itemsPerRow[prevBp];
      }
    }

    return itemsPerRow;
  },

  gutterClassToSpaceBetweenPx(gutterClass) {
    const bps = ["xs", "sm", "md", "lg", "xl", "xxl", "xxxl"];
    const out = Object.fromEntries(bps.map((bp) => [bp, undefined]));

    if (!gutterClass) {
      out.xs = 0;
      for (let i = 1; i < bps.length; i++) {
        out[bps[i]] = out[bps[i - 1]];
      }
      return out;
    }

    const tokens = gutterClass.trim().split(/\s+/);
    const re = /^g(?:-([a-z]{2,3}))?-(\d+)$/;

    tokens.forEach((tok) => {
      const m = re.exec(tok);
      if (!m) return;
      const bp = m[1] || "xs";
      const size = m[2];
      const rem = window.Stencil.getStencilVariable(`g-${size}`);
      const px = window.Stencil.remToPx(rem);
      out[bp] = px;
    });

    for (let i = 1; i < bps.length; i++) {
      if (out[bps[i]] == null) {
        out[bps[i]] = out[bps[i - 1]];
      }
    }

    if (out.xs == null) {
      out.xs = 0;
      for (let i = 1; i < bps.length; i++) {
        if (out[bps[i]] == null) {
          out[bps[i]] = out[bps[i - 1]];
        }
      }
    }

    return out;
  },

  getSwiperBreakpointsByColAndGutter(colClass, gutterClass) {
    const itemsPerRow = window.Stencil.colClassToItemsPerRow(colClass || "");
    const spacingPx = window.Stencil.gutterClassToSpaceBetweenPx(gutterClass || "");
    const bsBreakpoints = window.Stencil.getBSBreakpoints();

    const order = ["xs", "sm", "md", "lg", "xl", "xxl", "xxxl"];
    const out = {};

    order.forEach((bp) => {
      const key = bp === "xs" ? 0 : bsBreakpoints[bp];
      out[key] = {
        slidesPerView: itemsPerRow[bp] ?? 1,
        spaceBetween: spacingPx[bp] ?? 0,
      };
    });

    return out;
  },
};

window.Gauge = Gauge;
// window.DepartureBoard = DepartureBoard;

// Initialisation
AOS.init({
  once: true
});

// Imports from this folder - MP-specific
import MP from './mp';
window.MP = Object.assign(MP, window.MP);

import AutoAddProduct from './auto-add-product';
import BackInStock from './back-in-stock';
import Blog from './blog';
import Cart from './cart';
import Collection from './collection';
import ContentCollapse from './content-collapse';
import CookieBar from './cookie-bar';
import CountdownManager from './countdown-manager';
import Facets from './facets';
// import FGWES from './fgwes';
// import FGWP from './fgwp';
import Localization from './localization';
import ProductFormManager from './product-form-manager';
import ProductModels from './product-models';
import ProductTileManager from './product-tile-manager';
import SizeChartSocks from './size-chart-socks';
// import StickyPromoBanner from './sticky-promo-banner';
import Nav from './nav';
import Upsell from './upsell';

import PropositionsManager from './extensions/propositions-manager';
import ShopifyStorefrontAPI from './extensions/shopify-storefront-api';
try {
  MP.Extensions.PropositionsManager = new PropositionsManager();
  MP.Extensions.ShopifyStorefrontAPI = new ShopifyStorefrontAPI();
} catch (e) {
  console.error(e);
}

// Export for others scripts to use by attaching to the window
MP.Extensions.AutoAddProduct = AutoAddProduct; 
MP.Extensions.BackInStock = BackInStock;
MP.Extensions.Blog = Blog;
MP.Extensions.Cart = Cart;
MP.Extensions.Collection = Collection;
MP.Extensions.ContentCollapse = ContentCollapse;
MP.Extensions.CookieBar = CookieBar;
MP.Extensions.CountdownManager = CountdownManager;
MP.Extensions.Facets = Facets;
// MP.Extensions.FGWES = FGWES;
// MP.Extensions.FGWP = FGWP;
MP.Extensions.Localization = Localization;
MP.Extensions.ProductFormManager = ProductFormManager;
MP.Extensions.ProductModels = ProductModels;
MP.Extensions.ProductTileManager = ProductTileManager;
MP.Extensions.SizeChartSocks = SizeChartSocks;
// MP.Extensions.StickyPromoBanner = StickyPromoBanner;
MP.Extensions.Nav = Nav;
MP.Extensions.Upsell = Upsell;

/**
 * Load & define all custom elements in /elements using Webpack custom context
 */
const elementsContext = require.context('./elements', false, /\.js$/);
elementsContext.keys().forEach(file => {
  const moduleName = file.replace('./', '').replace('.js', ''); // Extract name from file
  const module = elementsContext(file);
  customElements.define(moduleName, module.default);
});

// Debug
console.log('[T] jQuery version is', $.fn.jquery);

// Global functions
window.debounce = function(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Kickoff!
MP.init();